import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Grid,
  List,
  ListItem,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ButtonIcon from 'components/ButtonIcon';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import Restrict from 'components/Restrict';
import Spinner from 'components/Spinner';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import { TypographyLocale } from 'components/TypographyLocale';
import UseAuthContext from 'context/AuthContext';
import UseLanguageContext from 'context/LanguageContext';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cancelInvitation,
  removeStaffbyInvitationID,
  resendInvitation,
} from 'services/business-setting-service';
import { USER_ROLES } from 'utils/constants';
import { user_authorizations } from 'utils/Formatter';
import { getErrorHtmlContent } from 'utils/utils';
import MemberInviteForm from './MemberInviteForm';

const BusinessStaff = ({ refetch, dataOrganization }: any) => {
  const {
    auth,
    getOrganization,
    role,
  } = useContext(UseAuthContext);
  const { language } = useContext(UseLanguageContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElInvite, setAnchorElInvite] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openInvite = Boolean(anchorElInvite);
  const [isLoading, setisLoading] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] = useState('');
  const isInspectorRole = role === USER_ROLES.INSPECTOR || role === USER_ROLES.INSPECTOR_MANAGER;
  const roles = [
    {
      title: t('BUSINESS_SETTING_MEMBER_PERMISSION_MANAGER'),
      description: t('BUSINESS_SETTING_MEMBER_PERMISSION_MANAGER_DESCRIPTION'),
    },
    {
      title: t('BUSINESS_SETTING_MEMBER_PERMISSION_MEMBER'),
      description: t('BUSINESS_SETTING_MEMBER_PERMISSION_MEMBER_DESCRIPTION'),
    },
    {
      title: t('BUSINESS_SETTING_MEMBER_PERMISSION_VIEWER'),
      description: t('BUSINESS_SETTING_MEMBER_PERMISSION_VIEWER_DESCRIPTION'),
    },
  ];

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id);
  };

  const handleMouseLeave = () => {
    setHoveredRow(null);
  };

  const handleClick = (event: any, id: string) => {
    setSelectedId(id);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickInvite = (event: any, id: string) => {
    setSelectedId(id);
    setAnchorElInvite(event.currentTarget);
  };

  const handleCloseInvite = () => {
    setAnchorElInvite(null);
  };

  const resendInvite = async (id: any) => {
    try {
      setisLoading(true);
      await resendInvitation(id);
      setisLoading(false);
      enqueueSnackbar(t('BUSINESS_STAFF_INVITE_RESEND_SUCCESS'), {
        variant: 'success',
      });
      handleCloseInvite();
    } catch (error: any) {
      if (error) {
        handleCloseInvite();
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.log('onError', error);
      }
      setisLoading(false);
    }
  };

  const cancelInvite = async (id: any) => {
    try {
      setisLoading(true);
      debugger;
      await cancelInvitation(id, dataOrganization?.id);
      await getOrganization(dataOrganization?.id);
      refetch();
      setisLoading(false);
      enqueueSnackbar(t('BUSINESS_STAFF_INVITE_CANCEL_SUCCESS'), {
        variant: 'success',
      });
      handleCloseInvite();
    } catch (error: any) {
      if (error) {
        handleCloseInvite();
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.log('onError', error);
      }
      setisLoading(false);
    }
  };

  const removeFromStaff = async (id: any) => {
    try {

      setisLoading(true);
      await removeStaffbyInvitationID(id);
      await getOrganization(dataOrganization?.id);
      refetch();
      setisLoading(false);
      enqueueSnackbar(t('BUSINESS_STAFF_REMOVE_SUCCESS'), {
        variant: 'success',
      });
      handleClose();
    } catch (error: any) {
      if (error) {
        handleClose();
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.log('onError', error);
      }
      setisLoading(false);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
  };

  const getSortedData = () => {
    if (!sortBy) return dataOrganization?.members || [];

    // Create a shallow copy for sorting
    const dataToSort = [...(dataOrganization?.members || [])];

    return dataToSort.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      // Consistent handling for null or undefined values:
      // Push them to the end of the list regardless of sort order.
      const aIsNull = aValue === null || aValue === undefined;
      const bIsNull = bValue === null || bValue === undefined;

      if (aIsNull && bIsNull) {
        return 0; // Both are null/undefined, consider them equal
      }
      if (aIsNull) {
        return 1; // a is null/undefined, b is not. a comes after b.
      }
      if (bIsNull) {
        return -1; // b is null/undefined, a is not. b comes after a (so a comes before b).
      }

      // At this point, neither aValue nor bValue is null or undefined.
      // Proceed with normal comparison.
      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }
      // If comparison is 0, they are equal according to < and >

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  if (isLoading) {
    return <Spinner></Spinner>;
  }


  return (
    <Grid >
      <Grid item className='business_staff_grid'>
        <Restrict forbidden={["viewer", "member", "inspector", "inspector_manager"]}>
          <MemberInviteForm members={dataOrganization?.members} refetch={refetch} />
        </Restrict>
      </Grid>
      <Box className="business_staff_grid">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "&:hover": {
                    boxShadow: "none",
                    border: "none",
                    backgroundColor: "transparent",
                  },
                }}
              >
                <CustomTableFontCell
                  sortDirection={sortBy === "name" ? sortOrder : false}
                >
                  <StyledTableSortLabel
                    active={sortBy === "name"}
                    direction={sortBy === "name" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "name")}
                  >
                    {t("BUSINESS_SETTING_MEMBER_PERMISSION_NAME")}
                    {sortBy === "name" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell
                  sortDirection={sortBy === "phone" ? sortOrder : false}
                >
                  <StyledTableSortLabel
                    active={sortBy === "phone"}
                    direction={sortBy === "phone" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "phone")}
                  >
                    {t("BUSINESS_SETTING_MEMBER_PERMISSION_PHONE")}
                    {sortBy === "phone" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell
                  sortDirection={sortBy === "email" ? sortOrder : false}
                >
                  <StyledTableSortLabel
                    active={sortBy === "email"}
                    direction={sortBy === "email" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "email")}
                  >
                    {t("BUSINESS_SETTING_MEMBER_PERMISSION_EMAIL")}
                    {sortBy === "email" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell
                  sortDirection={sortBy === "authorization" ? sortOrder : false}
                >
                  <StyledTableSortLabel
                    active={sortBy === "authorization"}
                    direction={sortBy === "authorization" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "authorization")}
                  >
                    {t("BUSINESS_SETTING_MEMBER_PERMISSION_ROLE")}
                    {sortBy === "authorization" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <Restrict forbidden={["viewer", "member", "inspector"]}>
                  <TableCell sx={{ width: '10%' }}></TableCell>
                </Restrict>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item: any) => {
                  return (
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      key={`members${item.id}`}
                      onMouseEnter={() => handleMouseEnter(item.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <CustomTableCell clickable={false} >{item.name}</CustomTableCell>
                      <CustomTableCell clickable={false} >{item.phone}</CustomTableCell>
                      <CustomTableCell clickable={false} >{item.email}</CustomTableCell>
                      <CustomTableCell clickable={false} >{t(user_authorizations.find(auth => auth.value === item.authorization)?.label)}</CustomTableCell>
                      <Restrict forbidden={["viewer", "member", "inspector"]}>
                        <CustomTableCell clickable={false} align="right">
                          {auth && auth?.id === item.id ? (
                            <></>
                          ) : (
                            <>
                              <ButtonIcon
                                isActive={open && selectedId === item.id}
                                aria-controls={open ? "long-menu" : undefined}
                                aria-expanded={open ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={(e) => handleClick(e, item.id)}
                              >
                                <MoreHorizIcon />
                              </ButtonIcon>
                              <Menu
                                sx={{ fontSize: '14px !important' }}
                                id="long-menu1"
                                MenuListProps={{
                                  "aria-labelledby": "long-button",
                                }}
                                anchorEl={anchorEl}
                                open={open && selectedId === item.id}
                                onClose={handleClose}
                              >
                                <MenuItem
                                  key="1"
                                  onClick={() => removeFromStaff(item.id)}
                                >
                                  {t("BUSINESS_SETTING_MEMBER_REMOVE_FROM_STAFF")}
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </CustomTableCell>
                      </Restrict>
                    </TableRow>
                  );
                })
              ) : (
                <></>
              )}

              {dataOrganization?.invites?.length > 0 ? (
                dataOrganization?.invites?.map((item) => {
                  return (
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      key={`members${item.id}`}
                      onMouseEnter={() => handleMouseEnter(item.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <CustomTableCell clickable={false} className="disable_font_color">
                        {t("INVITED_BY_TEAM")}
                      </CustomTableCell>
                      <CustomTableCell clickable={false} ></CustomTableCell>
                      <CustomTableCell clickable={false} className="disable_font_color">
                        {item.email}
                      </CustomTableCell>
                     
                      <CustomTableCell clickable={false} className="disable_font_color">
                        {t(user_authorizations
                          .find(auth => auth.value === item.authorization)?.label)}
                      </CustomTableCell>

                      <Restrict forbidden={["viewer", "member", "inspector"]}>
                        <CustomTableCell clickable={false} className="disable_font_color"  align="right">
                          <>
                            <ButtonIcon
                              aria-controls={openInvite ? "long-menu" : undefined}
                              aria-expanded={openInvite ? "true" : undefined}
                              aria-haspopup="true"
                              onClick={(e) => handleClickInvite(e, item.id)}
                            >
                              <MoreHorizIcon />
                            </ButtonIcon>
                            <Menu
                              id="long-menu"
                              MenuListProps={{
                                "aria-labelledby": "long-button",
                              }}
                              anchorEl={anchorElInvite}
                              open={openInvite && selectedId === item.id}
                              onClose={handleCloseInvite}
                              sx={{ fontSize: '14px !important' }}
                            >
                              <MenuItem
                                key="3"
                                onClick={() => resendInvite(item.id)}
                              >
                                {t("BUSINESS_SETTING_MEMBER_RESEND_INVITE")}
                              </MenuItem>
                              <MenuItem
                                key="4"
                                onClick={() => cancelInvite(item.id)}
                              >
                                {t("BUSINESS_SETTING_MEMBER_CANCEL_INVITE")}
                              </MenuItem>
                            </Menu>
                          </>
                        </CustomTableCell>
                      </Restrict>
                    </TableRow>
                  );
                })
              ) : (
                <></>
              )}
            </TableBody>
          </Table>{" "}
        </TableContainer>

      </Box>
      {!isInspectorRole && (
      <Grid sx={{ mt: 5 }}>
        <TypographyLocale
          language={language}
          variant="h3">{t('BUSINESS_SETTING_MEMBER_PERMISSION')}</TypographyLocale>
        <List>
          {roles.map((role, index) => (
            <ListItem key={index} sx={{ display: 'flex', gap: "10px", px: 0 }}>
              <PersonIcon />
              <Typography variant="subtitle1">
                <strong>{role.title} : </strong> {role.description}
              </Typography>
            </ListItem>
          ))}
          </List>
        </Grid>
      )}
    </Grid>
  );
};

export default BusinessStaff;
